'use client';

/**
 * PriceRangeSlider — dual-thumb slider for the catalog price filter (M-8).
 *
 * Wraps the project's existing shadcn `Slider` primitive (Radix-based —
 * supports array values for dual-thumb). The catalog filter state uses
 * string values where '' = "no filter on this boundary", so this wrapper
 * bridges:
 *   - slider min = 0     → output '' (no min filter — natural floor)
 *   - slider min ∈ (0,N] → output String(min)
 *   - slider max = 1000  → output '' (no max filter — natural ceiling)
 *   - slider max ∈ [0,N) → output String(max)
 *
 * Spec ref: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item M-8.
 *
 * §6 Anti-Invención (CR-CHECKLIST H2): the project already ships a generic
 *   Slider primitive (`@/components/ui/slider`, Radix-based). This file is
 *   a thin domain wrapper around it — NOT a parallel slider mechanism. The
 *   spec line "Since the project doesn't have a UI library slider component"
 *   is factually incorrect; the wrapper composes the existing primitive
 *   rather than duplicating it.
 *
 * Range: 0 → 1000 USDT (1 crédito = 1 USDT). Step: 10.
 * Mobile-friendly: thumb bumped to 24px (WCAG 2.5.5 Target Size — Enhanced)
 *   via Tailwind arbitrary variant on the slider-thumb data-slot.
 */
import { useCallback, useMemo } from 'react';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

const MIN_RANGE = 0;
const MAX_RANGE = 1000;
const STEP = 10;

export interface PriceRangeSliderProps {
  /** Min price in créditos ('' = no min filter — natural floor at 0). */
  minPrice: string;
  /** Max price in créditos ('' = no max filter — natural ceiling at 1000). */
  maxPrice: string;
  /** Controlled change handler. Receives the new (min, max) string pair. */
  onChange: (min: string, max: string) => void;
  className?: string;
}

/**
 * Clamp a raw string prop to the slider's numeric range. Returns the
 * boundary value when the prop is empty (sentinel for "no filter").
 */
function toSliderValue(raw: string, fallback: number): number {
  if (raw === '') return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(MIN_RANGE, Math.min(MAX_RANGE, n));
}

export function PriceRangeSlider({
  minPrice,
  maxPrice,
  onChange,
  className,
}: PriceRangeSliderProps) {
  // Slider receives numeric [min, max]. Out-of-range values (typed via the
  // fallback number inputs) are clamped to the slider's range so the thumb
  // stays visually inside the track. The label below still shows the raw
  // prop value so the user sees the actual filter applied.
  const sliderValue: [number, number] = useMemo(() => {
    const minNum = toSliderValue(minPrice, MIN_RANGE);
    const maxNum = toSliderValue(maxPrice, MAX_RANGE);
    // Defense-in-depth: ensure min ≤ max (Radix also enforces this).
    return [Math.min(minNum, maxNum), Math.max(minNum, maxNum)];
  }, [minPrice, maxPrice]);

  const handleValueChange = useCallback(
    (next: number[]) => {
      const [nextMin, nextMax] = next as [number, number];
      // Boundary values map back to '' (no filter). This keeps the
      // slider + the fallback number inputs + the active-filter badge
      // in sync: dragging max back to 1000 clears the max filter.
      const minOut = nextMin <= MIN_RANGE ? '' : String(nextMin);
      const maxOut = nextMax >= MAX_RANGE ? '' : String(nextMax);
      onChange(minOut, maxOut);
    },
    [onChange],
  );

  // Labels reflect the actual filter value (the prop), not the clamped
  // slider position, so out-of-range values typed in the fallback inputs
  // stay visible. '' is rendered as the natural boundary with a "+" hint
  // for max (signals "1000 or more") and "0" for min.
  const displayMin = minPrice === '' ? '0' : minPrice;
  const displayMax = maxPrice === '' ? `${MAX_RANGE}+` : maxPrice;

  return (
    <div className={cn('space-y-2.5', className)}>
      <Slider
        value={sliderValue}
        onValueChange={handleValueChange}
        min={MIN_RANGE}
        max={MAX_RANGE}
        step={STEP}
        // Bump the thumb to ≥24px for touch (WCAG 2.5.5). The default
        // size-4 (16px) is below the touch target guideline; the
        // arbitrary variant targets the slider-thumb data-slot exposed
        // by the primitive. Vertical padding gives the larger thumb
        // breathing room inside the sidebar.
        className="py-2.5 [&_[data-slot=slider-thumb]]:size-6 [&_[data-slot=slider-thumb]]:touch-none"
        aria-label="Rango de precio en créditos"
      />
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          <span className="sr-only">Precio mínimo: </span>
          {displayMin} créditos
        </span>
        <span aria-hidden="true" className="text-muted-foreground/50">—</span>
        <span>
          <span className="sr-only">Precio máximo: </span>
          {displayMax} créditos
        </span>
      </div>
    </div>
  );
}
